import React, { createContext, useContext, useMemo } from "react";
import useModules from "../hooks/useModules";

const ModuleScopeContext = createContext(null);

/**
 * Resolve a URL :moduleCode segment against the modules list.
 *
 * Matches (case-insensitive, accent-stripped, hyphen/space-normalized) against:
 *   1. module.code  (e.g. "AP", "IC", "CN")
 *   2. module.id    (UUID — fallback for hard-link sharing)
 *   3. module.name slug  (e.g. "ap-camp", "ic-camp", "construction")
 *
 * Returns the matched module or null. Logs a diagnostic warning on miss.
 */
function slug(s) {
    return String(s || "")
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim()
        .replace(/\s+/g, "-");
}

export function resolveModuleByCode(urlCode, modules) {
    if (!urlCode || !modules || modules.length === 0) return null;
    const target = slug(urlCode);
    let m = modules.find((mm) => slug(mm.code) === target);
    if (m) return m;
    m = modules.find((mm) => mm.id === urlCode);
    if (m) return m;
    m = modules.find((mm) => slug(mm.name) === target);
    if (m) return m;
    return null;
}

export function ModuleScopeProvider({ moduleCode, children }) {
    const { modules, byId, loading } = useModules();
    const module = useMemo(
        () => resolveModuleByCode(moduleCode, modules),
        [modules, moduleCode],
    );

    // Diagnostic logging on miss (helps debug prod issues like "Unknown module").
    if (!loading && !module && moduleCode && modules.length > 0) {
        // eslint-disable-next-line no-console
        console.warn("[ModuleScope] Failed to resolve URL code", {
            urlCode: moduleCode,
            normalised: slug(moduleCode),
            availableCodes: modules.map((m) => m.code),
            availableSlugs: modules.map((m) => slug(m.name)),
            count: modules.length,
        });
    }

    const value = useMemo(
        () => (module ? { module, moduleId: module.id, moduleCode: module.code, byId, loading: false } : null),
        [module, byId],
    );
    return <ModuleScopeContext.Provider value={value}>{children}</ModuleScopeContext.Provider>;
}

export function useModuleScope() {
    return useContext(ModuleScopeContext);
}

export function useScopedModuleId() {
    const scope = useModuleScope();
    return scope?.moduleId || null;
}
